/**
 * @fileoverview Provide shared multiline editing for caller-owned text. `AppTextarea` passes the new string to `onChange`; the PrimeReact-named `InputTextarea` export passes `{ target: { value } }` and maps `autoResize`, so migrated screens kept their handlers. The parent owns value and persistence, and decides on blur or save when edits become saved work.
 */

import React, { useLayoutEffect, useRef } from 'react'

const baseStyle = {
    width: '100%',
    padding: '0.6rem 0.75rem',
    borderRadius: '6px',
    border: '1px solid var(--surface-border)',
    fontSize: '1rem',
    resize: 'vertical',
    backgroundColor: 'var(--surface-section)',
    color: 'var(--text-color)',
    fontFamily: 'inherit',
    outline: 'none'
}

const borderlessStyle = {
    width: '100%',
    padding: '0.6rem 0',
    borderRadius: '0',
    border: 'none',
    borderBottom: '1px solid transparent',
    fontSize: '1rem',
    resize: 'none',
    backgroundColor: 'transparent',
    color: 'var(--text-color)',
    fontFamily: 'inherit',
    outline: 'none'
}



function AppTextarea({ id, value, onChange, rows, placeholder, className, variant, disabled, style: extraStyle, onBlur, autosize = false, ...textareaProps }) {
    const resolvedValue = value === null || value === undefined ? '' : value
    const isBorderless = variant === 'borderless'
    const textareaRef = useRef(null)
    const style = {
        ...(isBorderless ? borderlessStyle : baseStyle),
        cursor: disabled ? 'not-allowed' : undefined,
        opacity: disabled ? 0.6 : 1,
        overflowY: autosize ? 'hidden' : undefined,
        ...extraStyle
    }

    useLayoutEffect(

    () => {
        if (!autosize || !textareaRef.current) {
            return
        }

        const textarea = textareaRef.current
        textarea.style.height = '0px'
        textarea.style.height = `${textarea.scrollHeight}px`
    }, [autosize, resolvedValue])

    return (
        <textarea
            ref={textareaRef}
            id={id}
            value={resolvedValue}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            placeholder={placeholder}
            disabled={disabled}
            className={className || ''}
            style={style}
            {...textareaProps}
            onFocus={isBorderless ? (e) => { e.target.style.borderBottom = '1px solid var(--primary-color)' } : undefined}
            onBlur={(e) => {
                if (isBorderless) {
                    e.target.style.borderBottom = '1px solid transparent'
                }
                if (onBlur) {
                    onBlur(e)
                }
            }}
        />
    )
}

export default AppTextarea

export function InputTextarea({ onChange, autoResize, ...props }) {
  return <AppTextarea {...props} autosize={autoResize} onChange={value => onChange?.({ target: { value } })} />;
}
